// Calculate comprehensive wellness score based on all factors

export const calculateWellnessScore = (lifestyle, hormones, symptoms) => {
  let score = 50; // Base score
  let factors = [];
  
  // ============ LIFESTYLE FACTORS ============
  
  // Smoking (-15 to +5)
  if (lifestyle?.smoking_status === 'current') {
    score -= 15;
    factors.push({ name: 'Current Smoker', impact: -15, color: '#DC2626', category: 'lifestyle' });
  } else if (lifestyle?.smoking_status === 'former') {
    score -= 5;
    factors.push({ name: 'Former Smoker', impact: -5, color: '#F97316', category: 'lifestyle' });
  } else if (lifestyle?.smoking_status === 'never') {
    score += 5;
    factors.push({ name: 'Non-Smoker', impact: +5, color: '#10B981', category: 'lifestyle' });
  }
  
  // Exercise (-10 to +10)
  if (lifestyle?.exercise_frequency === 'daily') {
    score += 10;
    factors.push({ name: 'Daily Exercise', impact: +10, color: '#10B981', category: 'lifestyle' });
  } else if (lifestyle?.exercise_frequency === 'frequent' || lifestyle?.exercise_frequency === 'moderate') {
    score += 5;
    factors.push({ name: 'Regular Exercise', impact: +5, color: '#10B981', category: 'lifestyle' });
  } else if (lifestyle?.exercise_frequency === 'rarely') {
    score -= 5;
    factors.push({ name: 'Sedentary', impact: -5, color: '#F97316', category: 'lifestyle' });
  } else if (lifestyle?.exercise_frequency === 'never') {
    score -= 10;
    factors.push({ name: 'No Exercise', impact: -10, color: '#DC2626', category: 'lifestyle' });
  }
  
  // Sleep Quality - handles both numeric (1-10) and string values (-10 to +8)
  const sleepQuality = lifestyle?.sleep_quality;
  if (sleepQuality !== undefined && sleepQuality !== null) {
    if (typeof sleepQuality === 'number') {
      if (sleepQuality >= 8) {
        score += 8;
        factors.push({ name: 'Excellent Sleep', impact: +8, color: '#10B981', category: 'lifestyle' });
      } else if (sleepQuality >= 6) {
        score += 4;
        factors.push({ name: 'Good Sleep', impact: +4, color: '#10B981', category: 'lifestyle' });
      } else if (sleepQuality >= 4) {
        score -= 3;
        factors.push({ name: 'Fair Sleep', impact: -3, color: '#F97316', category: 'lifestyle' });
      } else {
        score -= 10;
        factors.push({ name: 'Poor Sleep', impact: -10, color: '#DC2626', category: 'lifestyle' });
      }
    } else {
      // String-based sleep quality
      if (sleepQuality === 'excellent') {
        score += 8;
        factors.push({ name: 'Excellent Sleep', impact: +8, color: '#10B981', category: 'lifestyle' });
      } else if (sleepQuality === 'good') {
        score += 4;
        factors.push({ name: 'Good Sleep', impact: +4, color: '#10B981', category: 'lifestyle' });
      } else if (sleepQuality === 'poor') {
        score -= 10;
        factors.push({ name: 'Poor Sleep', impact: -10, color: '#DC2626', category: 'lifestyle' });
      }
    }
  }
  
  // Alcohol Consumption (-12 to +3)
  if (lifestyle?.alcohol_consumption === 'heavy') {
    score -= 12;
    factors.push({ name: 'Heavy Alcohol', impact: -12, color: '#DC2626', category: 'lifestyle' });
  } else if (lifestyle?.alcohol_consumption === 'moderate') {
    score -= 4;
    factors.push({ name: 'Moderate Alcohol', impact: -4, color: '#F97316', category: 'lifestyle' });
  } else if (lifestyle?.alcohol_consumption === 'rarely') {
    score += 2;
    factors.push({ name: 'Light Alcohol', impact: +2, color: '#10B981', category: 'lifestyle' });
  } else if (lifestyle?.alcohol_consumption === 'never') {
    score += 3;
    factors.push({ name: 'No Alcohol', impact: +3, color: '#10B981', category: 'lifestyle' });
  }
  
  // BMI (-8 to +5)
  if (lifestyle?.bmi) {
    if (lifestyle.bmi >= 18.5 && lifestyle.bmi < 25) {
      score += 5;
      factors.push({ name: 'Healthy BMI', impact: +5, color: '#10B981', category: 'lifestyle' });
    } else if (lifestyle.bmi >= 25 && lifestyle.bmi < 30) {
      score -= 3;
      factors.push({ name: 'Overweight', impact: -3, color: '#F97316', category: 'lifestyle' });
    } else if (lifestyle.bmi >= 30) {
      score -= 8;
      factors.push({ name: 'Obesity', impact: -8, color: '#DC2626', category: 'lifestyle' });
    } else if (lifestyle.bmi < 18.5) {
      score -= 5;
      factors.push({ name: 'Underweight', impact: -5, color: '#F97316', category: 'lifestyle' });
    }
  }
  
  // ============ HORMONAL FACTORS ============
  
  // AMH - Anti-Müllerian Hormone (ovarian reserve indicator) (-12 to +8)
  if (hormones?.amh !== undefined && hormones.amh !== null) {
    if (hormones.amh < 0.5) {
      score -= 12;
      factors.push({ name: 'Very Low AMH', impact: -12, color: '#DC2626', category: 'hormonal' });
    } else if (hormones.amh < 1.0) {
      score -= 8;
      factors.push({ name: 'Low AMH', impact: -8, color: '#DC2626', category: 'hormonal' });
    } else if (hormones.amh >= 1.0 && hormones.amh < 2.0) {
      score += 3;
      factors.push({ name: 'Adequate AMH', impact: +3, color: '#10B981', category: 'hormonal' });
    } else if (hormones.amh >= 2.0 && hormones.amh <= 4.0) {
      score += 8;
      factors.push({ name: 'Optimal AMH', impact: +8, color: '#10B981', category: 'hormonal' });
    } else if (hormones.amh > 4.0) {
      score += 5; // Very high AMH can indicate PCOS, so slightly less bonus
      factors.push({ name: 'High AMH', impact: +5, color: '#3B82F6', category: 'hormonal' });
    }
  }
  
  // FSH - Follicle Stimulating Hormone (elevated = closer to menopause) (-10 to +5)
  if (hormones?.fsh !== undefined && hormones.fsh !== null) {
    if (hormones.fsh > 40) {
      score -= 10;
      factors.push({ name: 'Very High FSH', impact: -10, color: '#DC2626', category: 'hormonal' });
    } else if (hormones.fsh > 25) {
      score -= 6;
      factors.push({ name: 'Elevated FSH', impact: -6, color: '#F97316', category: 'hormonal' });
    } else if (hormones.fsh > 12.5) {
      score -= 2;
      factors.push({ name: 'Borderline FSH', impact: -2, color: '#F59E0B', category: 'hormonal' });
    } else if (hormones.fsh >= 3.5 && hormones.fsh <= 12.5) {
      score += 5;
      factors.push({ name: 'Normal FSH', impact: +5, color: '#10B981', category: 'hormonal' });
    }
  }
  
  // Estradiol (E2) - Low levels indicate declining ovarian function (-8 to +5)
  if (hormones?.estradiol !== undefined && hormones.estradiol !== null) {
    if (hormones.estradiol < 20) {
      score -= 8;
      factors.push({ name: 'Very Low Estradiol', impact: -8, color: '#DC2626', category: 'hormonal' });
    } else if (hormones.estradiol < 30) {
      score -= 4;
      factors.push({ name: 'Low Estradiol', impact: -4, color: '#F97316', category: 'hormonal' });
    } else if (hormones.estradiol >= 30 && hormones.estradiol <= 400) {
      score += 5;
      factors.push({ name: 'Normal Estradiol', impact: +5, color: '#10B981', category: 'hormonal' });
    } else if (hormones.estradiol > 400) {
      score -= 2; // Very high can indicate issues
      factors.push({ name: 'High Estradiol', impact: -2, color: '#F59E0B', category: 'hormonal' });
    }
  }
  
  // Inhibin B - Marker of ovarian reserve (-6 to +4)
  if (hormones?.inhibin_b !== undefined && hormones.inhibin_b !== null) {
    if (hormones.inhibin_b < 20) {
      score -= 6;
      factors.push({ name: 'Low Inhibin B', impact: -6, color: '#DC2626', category: 'hormonal' });
    } else if (hormones.inhibin_b >= 20 && hormones.inhibin_b <= 150) {
      score += 4;
      factors.push({ name: 'Normal Inhibin B', impact: +4, color: '#10B981', category: 'hormonal' });
    }
  }
  
  // ============ AGE FACTOR ============
  if (lifestyle?.age) {
    if (lifestyle.age < 40) {
      score += 8;
      factors.push({ name: 'Age < 40', impact: +8, color: '#10B981', category: 'demographic' });
    } else if (lifestyle.age >= 40 && lifestyle.age < 45) {
      score += 4;
      factors.push({ name: 'Age 40-44', impact: +4, color: '#10B981', category: 'demographic' });
    } else if (lifestyle.age >= 45 && lifestyle.age < 50) {
      score -= 2;
      factors.push({ name: 'Age 45-49', impact: -2, color: '#F59E0B', category: 'demographic' });
    } else if (lifestyle.age >= 50 && lifestyle.age < 55) {
      score -= 5;
      factors.push({ name: 'Age 50-54', impact: -5, color: '#F97316', category: 'demographic' });
    } else if (lifestyle.age >= 55) {
      score -= 8;
      factors.push({ name: 'Age 55+', impact: -8, color: '#DC2626', category: 'demographic' });
    }
  }
  
  // ============ SYMPTOM FACTORS ============
  // Hot flashes severity
  if (symptoms?.hot_flashes_severity) {
    if (symptoms.hot_flashes_severity === 'severe') {
      score -= 6;
      factors.push({ name: 'Severe Hot Flashes', impact: -6, color: '#DC2626', category: 'symptoms' });
    } else if (symptoms.hot_flashes_severity === 'moderate') {
      score -= 3;
      factors.push({ name: 'Moderate Hot Flashes', impact: -3, color: '#F97316', category: 'symptoms' });
    } else if (symptoms.hot_flashes_severity === 'mild') {
      score -= 1;
      factors.push({ name: 'Mild Hot Flashes', impact: -1, color: '#F59E0B', category: 'symptoms' });
    } else if (symptoms.hot_flashes_severity === 'none') {
      score += 3;
      factors.push({ name: 'No Hot Flashes', impact: +3, color: '#10B981', category: 'symptoms' });
    }
  }
  
  // Cycle regularity
  if (symptoms?.cycle_regularity) {
    if (symptoms.cycle_regularity === 'regular') {
      score += 5;
      factors.push({ name: 'Regular Cycles', impact: +5, color: '#10B981', category: 'symptoms' });
    } else if (symptoms.cycle_regularity === 'irregular') {
      score -= 4;
      factors.push({ name: 'Irregular Cycles', impact: -4, color: '#F97316', category: 'symptoms' });
    } else if (symptoms.cycle_regularity === 'absent') {
      score -= 8;
      factors.push({ name: 'Absent Cycles', impact: -8, color: '#DC2626', category: 'symptoms' });
    }
  }
  
  // Mood changes
  if (symptoms?.mood_changes) {
    if (symptoms.mood_changes === 'severe') {
      score -= 4;
      factors.push({ name: 'Severe Mood Changes', impact: -4, color: '#DC2626', category: 'symptoms' });
    } else if (symptoms.mood_changes === 'moderate') {
      score -= 2;
      factors.push({ name: 'Mood Changes', impact: -2, color: '#F97316', category: 'symptoms' });
    } else if (symptoms.mood_changes === 'none' || symptoms.mood_changes === 'stable') {
      score += 2;
      factors.push({ name: 'Stable Mood', impact: +2, color: '#10B981', category: 'symptoms' });
    }
  }
  
  // Clamp final score between 0 and 100
  return { score: Math.max(0, Math.min(100, score)), factors };
};

// Get wellness color based on score
export const getWellnessColor = (score) => {
  if (score >= 60) return '#10B981';
  if (score >= 40) return '#F59E0B';
  return '#DC2626';
};
